import React from 'react';
import { toast } from 'react-toastify';
import Router from 'next/router';
import { withTranslation } from 'next-i18next';
import { WithTranslation } from 'react-i18next';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { getCookie } from 'cookies-next';

import { KEYS } from '@utils/cache';
import { withAppContext, AppContextType } from '@context/AppContext';
import { API, APIRoutes } from '@utils/api';
import routes from '@utils/routes';

type Options = {
  redirectRoute?: string | undefined;
  shouldBeAuthorized: boolean;
};

interface AuthenticatedRouteProps extends WithTranslation {
  context: AppContextType;
  renderComponent: boolean;
}

function authenticatedRoute<P extends object>(
  Component: React.ComponentType<P> | null = null,
  options: Options = { shouldBeAuthorized: true },
) {
  class AuthenticatedRoute extends React.Component<P & AuthenticatedRouteProps> {
    override componentDidMount() {
      const {
        context: { isAuthenticated },
        t,
      } = this.props;

      const shouldRedirect = options.shouldBeAuthorized ? !isAuthenticated : isAuthenticated;

      if (shouldRedirect) {
        if (options.redirectRoute) {
          void Router.replace(options.redirectRoute);
        }
        if (options.shouldBeAuthorized) {
          toast(t('This content is visible only for authenticated users.'));
        }
      }
    }

    override componentDidUpdate(prevProps: P & AuthenticatedRouteProps) {
      const {
        context: { isAuthenticated },
        t,
      } = this.props;

      if (prevProps.context.isAuthenticated !== isAuthenticated) {
        const shouldRedirect = options.shouldBeAuthorized ? !isAuthenticated : isAuthenticated;

        if (shouldRedirect) {
          if (options.redirectRoute) {
            void Router.replace(options.redirectRoute);
          }
          if (options.shouldBeAuthorized) {
            toast(t('This content is visible only for authenticated users.'));
          }
        }
      }
    }

    override render() {
      if (!Component) {
        return null;
      }
      return <Component {...this.props} />;
    }
  }

  return withTranslation()(withAppContext(AuthenticatedRoute));
}

export function requireAuthentication(gssp: GetServerSideProps) {
  return async (context: GetServerSidePropsContext) => {
    const { req, res } = context;
    const token = getCookie(KEYS.AUTH_TOKEN, { req, res });

    if (token) {
      try {
        await API.get(APIRoutes.me);
      } catch {
        return {
          redirect: {
            destination: routes.signin,
            statusCode: 301,
          },
        };
      }
    } else {
      // Redirect to login page
      return {
        redirect: {
          destination: routes.signin,
          statusCode: 301,
        },
      };
    }

    return await gssp(context);
  };
}

export default authenticatedRoute;
